import type { Language } from '@offer-catalog/services/i18n'
import type { useSmModelsInfiniteQuery } from '@/hooks/products'
import type { SmModelItem } from '@/types/catalog'
import type { CatalogImage } from '@/types/catalog/common'
import { VpLoader } from '@vtmn-play/react'
import { VpIcon } from '@vtmn-play/react/headless'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NewSeasonBadge } from '@/components/NewSeasonBadge/NewSeasonBadge'
import { ProductImage } from '@/components/ProductImage/ProductImage'
import { SM_MODELS_PAGE_SIZE } from '@/hooks/products'

type ModelsQuery = ReturnType<typeof useSmModelsInfiniteQuery>
type FetchNextPageResult = Awaited<ReturnType<ModelsQuery['fetchNextPage']>>

async function fetchRemainingModelPages(fetchNextPage: ModelsQuery['fetchNextPage']): Promise<FetchNextPageResult> {
  const result = await fetchNextPage()

  if (result.hasNextPage && !result.isFetchNextPageError)
    return fetchRemainingModelPages(fetchNextPage)

  return result
}

interface ProductsListModelsProps {
  superModelId: number
  modelsQuery: Pick<ModelsQuery, 'fetchNextPage' | 'hasNextPage' | 'isError' | 'isLoading'>
  models: SmModelItem[]
  totalModels: number
  onPreviewChange: (preview: ProductsListModelPreview | undefined) => void
  onPreviewError: (preview: Omit<ProductsListModelPreview, 'image'>) => void
}

export interface ProductsListModelPreview {
  modelId: number
  modelName: string
  image?: CatalogImage | null
}

interface ProductsListModelTileProps extends Pick<ProductsListModelsProps, 'onPreviewChange' | 'onPreviewError'> {
  model: SmModelItem
  language: Language
}

function ProductsListModelTile({ model, language, onPreviewChange, onPreviewError }: Readonly<ProductsListModelTileProps>) {
  const [hasImageError, setHasImageError] = useState(false)
  const modelName = model.model.labels[language] ?? model.model.labels.en
  const handlePreviewStart = () => onPreviewChange({
    modelId: model.model.id,
    modelName,
    image: hasImageError ? undefined : model.image,
  })

  return (
    <li>
      <div
        className="products-list-row__model-tile"
        data-testid={`model-tile_${model.model.id}`}
        onPointerEnter={handlePreviewStart}
      >
        <div className="products-list-row__img-container">
          {model.is_new && (
            <NewSeasonBadge testId={`new-season_badge_${model.model.id}`} />
          )}
          <ProductImage
            alt={modelName}
            checkAvailability
            image={model.image}
            imageClassName="products-list-row__model-image"
            imageTestId={`model-image_${model.model.id}`}
            placeholderClassName="products-list-row__model-placeholder"
            placeholderTestId={`model-image-placeholder_${model.model.id}`}
            placeholderIconSize={24}
            width={125}
            onError={() => {
              setHasImageError(true)
              onPreviewError({ modelId: model.model.id, modelName })
            }}
          />
        </div>
        <div className="products-list-row__model-info">
          <span className="products-list-row__model-code" data-testid={`model-code_${model.model.id}`}>
            <span className="sr-only">{model.model.id.toString().split('').join(' ')}</span>
            <span aria-hidden="true">{model.model.id}</span>
          </span>
          <span className="products-list-row__model-name" data-testid={`model-name_${model.model.id}`}>
            {modelName}
          </span>
        </div>
      </div>
    </li>
  )
}

export function ProductsListModels({
  superModelId,
  modelsQuery,
  models,
  totalModels,
  onPreviewChange,
  onPreviewError,
}: Readonly<ProductsListModelsProps>) {
  const { t, i18n } = useTranslation()
  const language = i18n.language.split('-')[0] as Language
  const [isExpanded, setIsExpanded] = useState(false)
  const [isLoadingRemaining, setIsLoadingRemaining] = useState(false)
  const { fetchNextPage, hasNextPage, isError, isLoading } = modelsQuery
  const remainingCount = Math.max(0, totalModels - SM_MODELS_PAGE_SIZE)
  const visibleModels = isExpanded ? models : models.slice(0, SM_MODELS_PAGE_SIZE)
  const listId = `models-container_${superModelId}`

  const showAllModels = async () => {
    if (isLoadingRemaining)
      return

    if (!hasNextPage) {
      setIsExpanded(true)
      return
    }

    setIsLoadingRemaining(true)
    try {
      const result = await fetchRemainingModelPages(fetchNextPage)
      if (!result.isFetchNextPageError)
        setIsExpanded(true)
    }
    finally {
      setIsLoadingRemaining(false)
    }
  }

  const toggleExpanded = () => {
    if (isExpanded) {
      setIsExpanded(false)
      return
    }
    void showAllModels()
  }

  return (
    <ul
      id={listId}
      className="products-list-row__models"
      data-testid={listId}
      aria-busy={isLoading || isLoadingRemaining}
    >
      {isLoading && (
        <li role="status" className="products-list-row__model-loading">
          <VpLoader size="small" />
          <span className="sr-only">{t('products-page.products-list.loading-models')}</span>
        </li>
      )}
      {visibleModels.map(model => (
        <ProductsListModelTile
          key={model.model.id}
          model={model}
          language={language}
          onPreviewChange={onPreviewChange}
          onPreviewError={onPreviewError}
        />
      ))}
      {isError && models.length === 0 && (
        <li role="status" className="products-list-row__model-loading">
          {t('products-page.products-list.models-unavailable')}
        </li>
      )}
      {totalModels > SM_MODELS_PAGE_SIZE && (
        <li>
          <button
            type="button"
            className="products-list-row__show-more"
            data-testid={`show-${isExpanded ? 'less' : 'more'}_${superModelId}`}
            onClick={toggleExpanded}
            disabled={isLoadingRemaining}
            aria-controls={listId}
            aria-expanded={isExpanded}
            aria-label={isExpanded
              ? t('products-page.products-list.show-less')
              : t('products-page.products-list.show-models', { count: remainingCount })}
          >
            {isLoadingRemaining
              ? <VpLoader size="small" />
              : <VpIcon name={isExpanded ? 'subtract' : 'add'} />}
            {isExpanded
              ? t('products-page.products-list.show-less')
              : t('products-page.products-list.show-more', { count: remainingCount })}
          </button>
        </li>
      )}
    </ul>
  )
}
